import { Download, FileText, MoreHorizontal, Pencil, Reply, SmilePlus, Trash2, Volume2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { MessageWithSender, Profile } from '../types';
import { formatBytes, formatMessageTime } from '../lib/format';
import { getSignedAttachmentUrl } from '../lib/storage';
import Avatar from './Avatar';

function AttachmentPreview({ message }: { message: MessageWithSender }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!message.attachment_path || message.deleted_at) return;

    getSignedAttachmentUrl(message.attachment_path)
      .then(setUrl)
      .catch((attachmentError) =>
        setError(attachmentError instanceof Error ? attachmentError.message : 'Could not load attachment.'),
      );
  }, [message.attachment_path, message.deleted_at]);

  if (!message.attachment_path || message.deleted_at) return null;
  if (error) return <p className="text-sm text-relay-red">{error}</p>;
  if (!url) return <p className="text-sm text-slate-500">Loading attachment...</p>;

  if (message.kind === 'image') {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="mt-3 block max-w-lg overflow-hidden rounded-lg">
        <img src={url} alt={message.attachment_name || 'Uploaded image'} className="max-h-80 w-auto object-cover" />
      </a>
    );
  }

  if (message.kind === 'video') {
    return (
      <video controls className="mt-3 max-h-80 max-w-lg rounded-lg border border-white/10 bg-black">
        <source src={url} type={message.attachment_type || undefined} />
      </video>
    );
  }

  if (message.attachment_type?.startsWith('audio/')) {
    return (
      <div className="mt-3 max-w-md rounded-xl border border-white/10 bg-white/[0.04] p-3">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-white">
          <Volume2 size={17} className="text-relay-cyan" />
          {message.attachment_name || 'Voice message'}
        </div>
        <audio controls src={url} className="w-full" />
      </div>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="mt-3 flex max-w-md items-center gap-3 rounded-lg border border-white/10 bg-white/[0.04] p-3 text-sm text-slate-200 transition hover:bg-white/[0.07]"
    >
      <FileText className="text-relay-cyan" size={20} />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{message.attachment_name || 'Download file'}</span>
        <span className="text-xs text-slate-500">{formatBytes(message.attachment_size)}</span>
      </span>
      <Download size={18} />
    </a>
  );
}

export default function MessageBubble({
  message,
  currentProfile,
  isAdmin,
  onEdit,
  onDelete,
  onReply,
  onProfileClick,
  readReceipt,
}: {
  message: MessageWithSender;
  currentProfile: Profile;
  isAdmin: boolean;
  onEdit: (messageId: string, content: string) => Promise<void>;
  onDelete: (messageId: string) => Promise<void>;
  onReply: (message: MessageWithSender) => void;
  onProfileClick: (profile: Profile) => void;
  readReceipt?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.content || '');
  const [busy, setBusy] = useState(false);
  const [reactions, setReactions] = useState<Record<string, number>>({ '👍': 0, '🔥': 0, '✅': 0 });
  const canManage = !message.deleted_at && (message.sender_id === currentProfile.id || isAdmin);
  const sender = message.sender;

  async function submitEdit() {
    if (!draft.trim()) return;
    setBusy(true);
    try {
      await onEdit(message.id, draft.trim());
      setEditing(false);
    } finally {
      setBusy(false);
    }
  }

  async function submitDelete() {
    if (!window.confirm('Delete this message?')) return;
    setBusy(true);
    try {
      await onDelete(message.id);
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="group relative grid gap-3 px-3 py-3 transition hover:bg-white/[0.03] sm:grid-cols-[40px_minmax(0,1fr)] sm:px-4">
      {sender ? (
        <button
          type="button"
          onClick={() => onProfileClick(sender)}
          className="self-start rounded-lg transition hover:scale-105"
          aria-label={`Open ${sender.display_name} profile`}
        >
          <Avatar name={sender.display_name} src={sender.avatar_url} />
        </button>
      ) : (
        <Avatar name="Unknown" />
      )}
      <div className="min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="font-semibold text-white">{sender?.display_name || 'Deleted user'}</span>
          <span className="text-xs text-slate-500">{formatMessageTime(message.created_at)}</span>
          {message.edited_at && !message.deleted_at && <span className="text-xs text-slate-600">(edited)</span>}
        </div>

        {message.deleted_at ? (
          <p className="mt-1 text-sm italic text-slate-600">This message was deleted.</p>
        ) : editing ? (
          <div className="mt-2 space-y-2">
            <textarea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              rows={3}
              className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={submitEdit}
                disabled={busy}
                className="rounded-md bg-relay-blue px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="rounded-md border border-white/10 px-3 py-1.5 text-xs font-semibold text-slate-300"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            {message.content && (
              <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-6 text-slate-200">{message.content}</p>
            )}
            <AttachmentPreview message={message} />
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {Object.entries(reactions)
                .filter(([, count]) => count > 0)
                .map(([emoji, count]) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => setReactions((current) => ({ ...current, [emoji]: Math.max(0, current[emoji] - 1) }))}
                    className="rounded-full border border-relay-cyan/20 bg-relay-cyan/10 px-2.5 py-1 text-xs text-slate-200 transition hover:bg-relay-cyan/15"
                  >
                    {emoji} {count}
                  </button>
                ))}
              {readReceipt && <span className="text-xs text-slate-600">{readReceipt}</span>}
            </div>
          </>
        )}
      </div>

      {!message.deleted_at && (
        <div className="absolute right-3 top-2 hidden overflow-hidden rounded-xl border border-white/10 bg-ink-750 shadow-panel group-hover:flex">
          <button
            type="button"
            onClick={() => onReply(message)}
            className="grid h-8 w-8 place-items-center text-slate-400 transition hover:bg-white/10 hover:text-white"
            aria-label="Reply to message"
            title="Reply"
          >
            <Reply size={14} />
          </button>
          <button
            type="button"
            onClick={() => setReactions((current) => ({ ...current, '👍': current['👍'] + 1 }))}
            className="grid h-8 w-8 place-items-center text-slate-400 transition hover:bg-white/10 hover:text-white"
            aria-label="React to message"
            title="React"
          >
            <SmilePlus size={14} />
          </button>
          {canManage && (
            <>
          <button
            type="button"
            onClick={() => setEditing(true)}
            disabled={busy || !message.content}
            className="grid h-8 w-8 place-items-center text-slate-400 transition hover:bg-white/10 hover:text-white disabled:opacity-40"
            aria-label="Edit message"
            title="Edit"
          >
            <Pencil size={14} />
          </button>
          <button
            type="button"
            onClick={submitDelete}
            disabled={busy}
            className="grid h-8 w-8 place-items-center text-slate-400 transition hover:bg-relay-red/15 hover:text-relay-red"
            aria-label="Delete message"
            title="Delete"
          >
            <Trash2 size={14} />
          </button>
            </>
          )}
          <span className="grid h-8 w-8 place-items-center text-slate-600">
            <MoreHorizontal size={14} />
          </span>
        </div>
      )}
    </article>
  );
}
